/**
 * MoreButton.tsx
 * -------------------------
 * Reusable "..." button that displays a Fluent UI Menu. You pass in
 * an array of items. Each item can be hidden or visible based on conditions.
 */
import { Menu, MenuItem, MenuList, MenuPopover, MenuTrigger, Tooltip } from '@fluentui/react-components';
import { MoreHorizontal24Regular } from '@fluentui/react-icons';
import { TMoreButtonItem } from '../../types';
import { useState } from 'react';
import { ControlledTooltip } from '@shared/components';

type TProps = {
  items: TMoreButtonItem[];
};

export const MoreButton = ({ items }: TProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const visibleItems = items.filter((item) => !item.hidden);

  if (visibleItems.length === 0) return null;

  return (
    <Menu
      open={isOpen}
      onOpenChange={(e, data) => {
        // Avoid unselecting the comment card
        e.stopPropagation();
        setIsOpen(data.open);
      }}
      positioning={{ autoSize: true }}
    >
      <MenuTrigger disableButtonEnhancement>
        <Tooltip content="More actions" relationship="label" withArrow>
          <MoreHorizontal24Regular />
        </Tooltip>
      </MenuTrigger>

      <MenuPopover>
        <MenuList>
          {visibleItems.map((item) => (
            <ControlledTooltip
              relationship="inaccessible"
              content={item.disabledTooltip || ''}
              key={item.key}
              enable={Boolean(item.disabled)}
              withArrow
            >
              <MenuItem
                key={item.key}
                icon={<item.icon />}
                onClick={(e) => {
                  // Avoid unselecting the comment card
                  e.stopPropagation();
                  if (item.disabled) return;
                  setIsOpen(false);
                  item.onClick();
                }}
                disabled={item.disabled}
              >
                {item.label}
              </MenuItem>
            </ControlledTooltip>
          ))}
        </MenuList>
      </MenuPopover>
    </Menu>
  );
};

export default MoreButton;
